import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../config/axiosConfig';
import { useAuth } from '../context/AuthContext';
import { TicketData, TicketStatus, User, Attachment, TicketPriority } from '../types';
import { ticketStatusTranslations, ticketPriorityTranslations } from '../utils/traslations';
import { formatLocalDate } from '../utils/dateFormatter';
import CommentForm from '../components/Common/CommentForm';
import CommentBody from '../components/Common/CommentBody';
import { isInternalComment } from '../utils/commentHtml';
import { postTicketComment, commentErrorMessage } from '../utils/ticketComments';
import ContactPhoneRow from '../components/Common/ContactPhoneRow';
import { InternalTaskBadge, isTicketInternalTask } from '../components/Tickets/InternalTaskBadge';
import TicketWorklogSidebar from '../components/Tickets/TicketWorklogSidebar';
import TicketGithubActivity from '../components/Tickets/TicketGithubActivity';
import TicketAuthorizationBanner from '../components/Tickets/TicketAuthorizationBanner';
import {
    staffAssignableUsers,
    ticketRealHoursValid,
    ticketRequiresRealHoursForClosure,
} from '../utils/ticketAccess';
import { clCard, clInput, clModalPanel } from '../utils/cleanLightUi';

type DetailedTicketData = TicketData & {
    ticket_category_name?: string;
    ticket_department_name?: string;
    attachments?: Attachment[];
};

const FileIcon: React.FC<{ className?: string }> = ({ className = "w-16 h-16" }) => (
    <svg className={`${className} mx-auto text-gray-400 mb-2`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
    </svg>
);

const AdminTicketDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [ticket, setTicket] = useState<DetailedTicketData | null>(null);
    const [agents, setAgents] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // ✅ AÑADIDO: Estado para el modal de confirmación de borrado
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const agentsForReassign = useMemo(
        () => staffAssignableUsers(agents, user ?? undefined),
        [agents, user]
    );

    const fetchTicketDetails = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const [ticketRes, agentsRes] = await Promise.all([
                api.get(`/api/tickets/${id}`),
                api.get('/api/users/agents')
            ]);
            setTicket(ticketRes.data.data); 
            setAgents(agentsRes.data.data);
        } catch (err) {
            setError('No se pudo cargar el ticket o no tienes permiso para verlo.');
            toast.error('Error al cargar los datos del ticket.');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchTicketDetails();
    }, [fetchTicketDetails]);

    const handleAddComment = async (commentText: string, isInternal: boolean, images: File[] = []) => {
        if (!ticket) return;
        try {
            await postTicketComment(ticket.id, commentText, isInternal, images);
            toast.success('Comentario añadido exitosamente.');
            fetchTicketDetails();
        } catch (err) {
            toast.error(commentErrorMessage(err));
        }
    };
    
    const handleStatusChange = async (newStatus: TicketStatus) => {
        if (!ticket) return;
        const needsHoursClosure = ticketRequiresRealHoursForClosure(
            ticket.ticket_department_name ?? ticket.department_name,
            ticket.es_tarea_interna
        );
        const staffNeedsRealHours =
            user?.role !== 'client' &&
            (newStatus === 'resolved' || newStatus === 'closed') &&
            needsHoursClosure;
        if (staffNeedsRealHours && !ticketRealHoursValid(ticket.horas_reales)) {
            toast.warn(
                'Para este ticket (Desarrollo o tarea interna) registrá las horas reales en «Registro de trabajo» antes de resolver o cerrar.'
            );
            return;
        }
        try {
            await api.put(`/api/tickets/${ticket.id}/status`, { status: newStatus });
            toast.success(`El estado del ticket se actualizó a "${ticketStatusTranslations[newStatus] || newStatus}".`);
            fetchTicketDetails();
        } catch (error) {
            toast.error("No se pudo actualizar el estado del ticket.");
        }
    };

    const handlePriorityChange = async (newPriority: TicketPriority) => {
        if (!ticket) return;
        try {
            await api.put(`/api/tickets/${ticket.id}`, { priority: newPriority });
            toast.success(`La prioridad del ticket se actualizó a "${ticketPriorityTranslations[newPriority] || newPriority}".`);
            fetchTicketDetails();
        } catch (error) {
            toast.error("No se pudo actualizar la prioridad del ticket.");
        }
    };

    const handleReassignTicket = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        const newAgentId = formData.get('agentId');
        if (!newAgentId || !ticket) return;

        try {
            await api.put(`/api/tickets/${ticket.id}/reassign`, { newAgentId });
            toast.success('Ticket reasignado exitosamente.');
            fetchTicketDetails();
        } catch (error) {
            toast.error('Error al reasignar el ticket.');
        }
    };

    // ✅ AÑADIDO: Función para eliminar el ticket
    const handleDeleteTicket = async () => {
        if (!ticket) return;
        try {
            await api.delete(`/api/tickets/${ticket.id}`);
            toast.success('Ticket eliminado exitosamente.');
            setIsDeleteModalOpen(false);
            navigate('/admin/tickets'); // Redirigir a la lista de tickets
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Error al eliminar el ticket.');
            setIsDeleteModalOpen(false);
        }
    };
    // FIN MODIFICACIÓN

    if (loading) return <div className="p-8 text-center">Cargando ticket...</div>;
    if (error) return <div className="p-8 text-center text-red-500">{error}</div>;
    
    if (!ticket) return <div className="p-8 text-center">Ticket no encontrado.</div>;

    const showWorklogSidebar =
        (user?.role === 'admin' || user?.role === 'agent') &&
        ticketRequiresRealHoursForClosure(
            ticket.ticket_department_name ?? ticket.department_name,
            ticket.es_tarea_interna
        );

    return (
        <>
            <div className="container mx-auto p-4 sm:p-6 lg:p-8">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6">
                    <div className="min-w-0 flex flex-wrap items-center gap-3">
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 break-words">
                            Ticket #{ticket.id}: {ticket.title}
                        </h1>
                        {isTicketInternalTask(ticket) && <InternalTaskBadge />}
                    </div>
                    <button type="button" onClick={() => navigate(-1)} className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2.5 px-5 rounded-xl shrink-0 self-start shadow-sm">Volver</button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Columna Izquierda: Detalles y Conversación */}
                    <div className="lg:col-span-2 space-y-6">
                        <TicketAuthorizationBanner ticketId={ticket.id} canResend refreshKey={ticket.updated_at} />
                        <div className={`${clCard} p-6`}>
                            <h2 className="text-xl font-bold text-gray-800 mb-4 border-b border-gray-100 pb-2">Detalles del Ticket</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                                <div className="min-w-0 break-words"><strong className="block text-gray-500">Cliente:</strong> {ticket.client_name}</div>
                                <ContactPhoneRow phone={ticket.telefono_contacto} />
                                <div><strong className="block text-gray-500">Departamento:</strong> {ticket.ticket_department_name ? `${ticket.ticket_department_name} (${ticket.department_id})` : ticket.department_id}</div>
                                <div><strong className="block text-gray-500">Categoría:</strong> {ticket.ticket_category_name || 'N/A'}</div>
                                {ticket.subcategoria ? (
                                    <div><strong className="block text-gray-500">Sub-categoría:</strong> {ticket.subcategoria}</div>
                                ) : null}
                                {ticket.github_repo && String(ticket.github_repo).trim() !== '' ? (
                                    <div>
                                        <strong className="block text-gray-500">Repo GitHub:</strong>{' '}
                                        <span className="font-mono text-sm break-all">{String(ticket.github_repo).trim()}</span>
                                    </div>
                                ) : null}
                                <div><strong className="block text-gray-500">Creado:</strong> {formatLocalDate(ticket.created_at)}</div>
                            </div>
                            {user?.role === 'admin' &&
                                [ticket.horas_estimadas, ticket.horas_reales].some((h) => h !== undefined && h !== null && h !== '') && (
                                    <p className="mt-4 text-sm text-gray-600 border-t pt-3">
                                        <strong className="text-gray-700">Horas:</strong>{' '}
                                        {ticket.horas_estimadas !== undefined && ticket.horas_estimadas !== null && ticket.horas_estimadas !== '' && (
                                            <>Estimadas {Number(ticket.horas_estimadas)} h</>
                                        )}
                                        {ticket.horas_estimadas !== undefined && ticket.horas_estimadas !== null && ticket.horas_estimadas !== '' &&
                                            ticket.horas_reales !== undefined && ticket.horas_reales !== null && ticket.horas_reales !== '' &&
                                            ' · '}
                                        {ticket.horas_reales !== undefined && ticket.horas_reales !== null && ticket.horas_reales !== '' && (
                                            <>Reales {Number(ticket.horas_reales)} h</>
                                        )}
                                    </p>
                                )}
                            <h3 className="text-lg font-semibold mt-6 mb-2">Descripción</h3>
                            <p className="text-gray-700 whitespace-pre-wrap break-words">{ticket.description}</p>
                        </div>

                        {ticket.attachments && ticket.attachments.length > 0 && (
                            <div className={`${clCard} p-6`}>
                                <h2 className="text-xl font-bold text-gray-800 mb-4 border-b border-gray-100 pb-2">Archivos Adjuntos</h2>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                    {ticket.attachments.map(att => (
                                        <a 
                                            key={att.id}
                                            href={`/${att.file_path.replace(/\\/g, '/')}`} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="border border-gray-200 rounded-xl p-2 text-center hover:bg-gray-50/80 transition-colors group bg-white"
                                            title={`Ver: ${att.file_name}`}
                                        >
                                            {att.file_type && att.file_type.startsWith('image/') ? (
                                                <img src={`/${att.file_path.replace(/\\/g, '/')}`} alt={att.file_name} className="w-full h-24 object-cover rounded-md mb-2"/>
                                            ) : att.file_type && att.file_type.startsWith('video/') ? (
                                                <div className="w-full h-24 bg-black rounded-md mb-2 flex items-center justify-center">
                                                    <svg className="w-10 h-10 text-white opacity-75" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd"></path>
                                                    </svg>
                                                </div>
                                            ) : (
                                                <FileIcon className="w-full h-24" />
                                            )}
                                            <p className="text-sm text-gray-700 truncate group-hover:underline">
                                                {att.file_name}
                                            </p>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}

                        {ticket.github_repo && String(ticket.github_repo).trim() !== '' && (
                            <TicketGithubActivity
                                ticketId={ticket.id}
                                githubRepo={String(ticket.github_repo).trim()}
                            />
                        )}

                        <div className={`${clCard} p-6`}>
                            <h2 className="text-xl font-bold text-gray-800 mb-4 border-b border-gray-100 pb-2">Conversación</h2>
                            <div className="space-y-5 mb-6 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                                {ticket.comments && ticket.comments.length > 0 ? ticket.comments.map(comment => (
                                    <div
                                        key={comment.id}
                                        className={`p-4 rounded-xl ${
                                            isInternalComment(comment)
                                                ? 'bg-slate-50 border border-slate-200/90'
                                                : 'bg-white border border-gray-100 shadow-sm'
                                        }`}
                                    >
                                        <div className="flex justify-between items-center text-xs text-gray-500 gap-2">
                                            <span className="font-bold text-gray-700">{comment.username || 'Sistema'}</span>
                                            <span className="shrink-0">{formatLocalDate(comment.created_at)}</span>
                                        </div>
                                        <CommentBody text={comment.comment_text} />
                                        {isInternalComment(comment) && (
                                            <span className="text-xs font-bold text-amber-700 mt-2 block">NOTA INTERNA</span>
                                        )}
                                    </div>
                                )) : (
                                    <p className="text-center text-gray-500 py-8">No hay comentarios aún.</p>
                                )}
                            </div>
                            {ticket.status !== 'closed' && user && <CommentForm onAddComment={handleAddComment} userRole={user.role} />}
                        </div>
                    </div>

                    {/* Columna Derecha: Acciones */}
                    <div className="lg:col-span-1 space-y-6">
                        {showWorklogSidebar && (
                            <TicketWorklogSidebar
                                ticketId={ticket.id}
                                horasReales={ticket.horas_reales}
                                status={ticket.status}
                                departmentName={ticket.ticket_department_name ?? ticket.department_name ?? null}
                                esTareaInterna={ticket.es_tarea_interna}
                                disabled={ticket.status === 'closed'}
                                onSaved={fetchTicketDetails}
                            />
                        )}
                        <div className={`${clCard} p-6`}>
                            <h2 className="text-xl font-bold text-gray-800 mb-4 border-b border-gray-100 pb-2">Estado y Prioridad</h2>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Estado Actual</label>
                                    <select 
                                        value={ticket.status}
                                        onChange={(e) => handleStatusChange(e.target.value as TicketStatus)}
                                        className={`mt-1 block w-full ${clInput}`}
                                        disabled={ticket.status === 'closed'}
                                    >
                                        <option value="open">Abierto</option>
                                        <option value="in-progress">En Progreso</option>
                                        <option value="resolved">Resuelto</option>
                                        <option value="closed">Cerrado</option>
                                    </select>
                                </div>
                                
                                <div>
                                    <label htmlFor="priority-select" className="block text-sm font-medium text-gray-700">Prioridad</label>
                                    <select 
                                        id="priority-select"
                                        value={ticket.priority}
                                        onChange={(e) => handlePriorityChange(e.target.value as TicketPriority)}
                                        className={`mt-1 block w-full ${clInput}`}
                                        disabled={ticket.status === 'closed'}
                                    >
                                        {Object.entries(ticketPriorityTranslations).map(([key, value]) => (
                                            <option key={key} value={key}>{value}</option>
                                        ))}
                                    </select>
                                </div>

                            </div>
                        </div>

                        <div className={`${clCard} p-6`}>
                            <h2 className="text-xl font-bold text-gray-800 mb-4 border-b border-gray-100 pb-2">Agente Asignado</h2>
                            <p className="font-semibold text-lg">{ticket.agent_names || ticket.agent_name || 'Sin asignar'}</p>
                        </div>

                        <div className={`${clCard} p-6`}>
                            <h2 className="text-xl font-bold text-gray-800 mb-4 border-b border-gray-100 pb-2">Reasignar Ticket</h2>
                            <form onSubmit={handleReassignTicket}>
                                <select name="agentId" className={`w-full ${clInput}`} defaultValue="">
                                    <option value="" disabled>-- Selecciona un agente --</option>
                                    {agentsForReassign.map((agent) => (
                                        <option key={agent.id} value={agent.id}>
                                            {agent.first_name && agent.last_name ? `${agent.first_name} ${agent.last_name}` : agent.username}
                                        </option>
                                    ))}
                                </select>
                                <button type="submit" className="w-full bg-red-600 text-white font-semibold py-2.5 px-4 rounded-xl mt-4 hover:bg-red-700 shadow-sm">
                                    Reasignar
                                </button>
                            </form>
                        </div>

                        {/* ✅ AÑADIDO: Botón de Eliminar Ticket (Solo para Admin) */}
                        {user?.role === 'admin' && (
                            <div className={`${clCard} p-6 border-red-200 ring-1 ring-red-100`}>
                                <h2 className="text-xl font-bold text-red-700 mb-4">Zona de Peligro</h2>
                                <button 
                                    type="button"
                                    onClick={() => setIsDeleteModalOpen(true)}
                                    className="w-full bg-red-700 hover:bg-red-800 text-white font-semibold py-2.5 px-4 rounded-xl shadow-sm"
                                >
                                    Eliminar Ticket Permanentemente
                                </button>
                                <p className="text-xs text-gray-600 mt-2 text-center">Esta acción no se puede deshacer.</p>
                            </div>
                        )}
                        {/* FIN MODIFICACIÓN */}

                    </div>
                </div>
            </div>

            {/* ✅ AÑADIDO: Modal de Confirmación de Borrado */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
                    <div className={`${clModalPanel} max-w-md p-6`}>
                        <h3 className="text-2xl font-bold text-red-700">Confirmar Eliminación</h3>
                        <p className="my-4 text-gray-700">
                            ¿Estás absolutamente seguro de que quieres eliminar el ticket #{ticket.id}?
                            <br />
                            <strong className="font-bold">Esta acción es irreversible</strong> y se borrarán todos los comentarios y adjuntos asociados.
                        </p>
                        <div className="flex justify-end gap-4 mt-6 flex-wrap">
                            <button type="button" onClick={() => setIsDeleteModalOpen(false)} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold px-5 py-2.5 rounded-xl">
                                Cancelar
                            </button>
                            <button type="button" onClick={handleDeleteTicket} className="bg-red-600 hover:bg-red-700 text-white font-semibold px-5 py-2.5 rounded-xl shadow-sm">
                                Sí, eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AdminTicketDetailPage;

